import { Fragment, HTMLAttributes, Key, ReactNode, useEffect, useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteRenderGetTagProps } from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { useQuery } from '@apollo/client';
import _ from 'lodash';
import { Box, Chip, IconButton, Link } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import useDebounce from '../../hooks/useDebounce';
import { portModel } from 'model';
import { useApolloErrorHandler } from 'hooks';
import { GET_PORTS, GetPortsOutput } from 'api';
import { TXT_AND_MORE, TXT_CREATE_PORT } from '../../../../../shared/translations';
import capitalize from '../../../../../shared/stringUtils/capitalize';
import capitalizeEveryWord from '../../../../../shared/stringUtils/capitalizeEveryWord';
import { PortEditor } from 'pages/Ports/PortEditor';
import { useTranslation } from 'react-i18next';

export interface PortSelectorProps {
  id?: string;
  port?: portModel;
  ports?: portModel[];
  portIds?: (string | undefined)[];
  label?: string;
  error?: boolean;
  showAllSelection?: boolean;
  onSelectedChange?: (port: portModel | null) => void;
  onSelectedPortsChange?: (ports: portModel[] | null) => void;
  disabled?: boolean;
  country?: string;
  allowAdd?: boolean;
  multiple?: boolean;
  errorMessage?: string;
  mustHaveCoordinates?: boolean;
  excludePortIds?: string[];
}

const VISIBLE_OPTIONS_COUNT = 5;

export const PortSelector = (props: PortSelectorProps) => {
  const [selected, setSelected] = useState<portModel | null>(props.port || null);
  const [selectedPorts, setSelectedPorts] = useState<portModel[]>(props.ports ? [...props.ports] : []);
  const [text, setText] = useState<string | undefined>(undefined);
  const [options, setOptions] = useState<portModel[]>([]);
  const { apolloErrorHandler } = useApolloErrorHandler();
  const { t } = useTranslation();
  const [openCreate, setOpenCreate] = useState(false);

  const debouncedText = useDebounce((txt: string | undefined) => setText(txt));

  const filter = useMemo(() => {
    let result: unknown[] = [];
    if ((text || '').trim().length > 0) {
      result = [
        ...result,
        {
          name: {
            contains: text
          }
        }
      ];
    }

    if (props.country) {
      result = [
        ...result,
        {
          country: {
            eq: props.country
          }
        }
      ];
    }

    if (props.excludePortIds && props.excludePortIds.length > 0) {
      result = [
        ...result,
        {
          id: {
            nin: props.excludePortIds
          }
        }
      ];
    }

    if (result.length === 0) {
      return null;
    }

    return result.length === 1 ? result[0] : { and: result };
  }, [text, props.country, props.excludePortIds]);

  const { loading, data, error, refetch } = useQuery<GetPortsOutput>(GET_PORTS, {
    fetchPolicy: 'network-only',
    skip: !!props.disabled,
    variables: {
      where: filter,
      take: VISIBLE_OPTIONS_COUNT,
      skip: 0
    }
  });

  const onInputChange = (_: object, value: string) => {
    debouncedText(value);
  };

  const onSelectedChange = (_: object, value: portModel | portModel[] | null) => {
    if (props.multiple) {
      const newValue = value !== null && Array.isArray(value) ? [...value] : [];
      setSelectedPorts(() => newValue);
      if (props.onSelectedPortsChange) {
        props.onSelectedPortsChange(newValue);
      }
    } else {
      const newValue = value !== null && !Array.isArray(value) ? { ...value } : null;
      setSelected(() => newValue);
      if (props.onSelectedChange) {
        props.onSelectedChange(newValue);
      }
    }
  };

  const openCloseEditor = () => {
    setOpenCreate((prev) => !prev);
  };

  const noOption = (): ReactNode => {
    if (!props.allowAdd || !text || text.trim().length <= 0) {
      return 'No options';
    }

    return (
      <Link component="button" variant="inherit" onClick={openCloseEditor}>
        {`${capitalize(t(TXT_CREATE_PORT))} '${text}'`}
      </Link>
    );
  };

  const onCreated = (newPort?: portModel) => {
    if (!newPort) {
      return;
    }

    setOptions((prev) => [...prev, newPort]);
    onSelectedChange(_, props.multiple ? [...selectedPorts, newPort] : newPort);
  };

  const renderPortOption = (propsLi: HTMLAttributes<HTMLLIElement> & { key: Key }, option: portModel) => {
    return (
      <Box component="li" {...propsLi} key={option.id}>
        {props.mustHaveCoordinates &&
          option.id !== 'port-hidden-options-remaining' &&
          (!option.latitude || !option.longitude) && (
          <WarningAmberIcon fontSize="inherit" color="warning" sx={{ mr: 1 }} />
        )}
        <div>{`${option.name} ${option.id === 'port-hidden-options-remaining' ? '' : `(${option.country})`}`}</div>
      </Box>
    );
  };

  const renderTags = () => {
    if (!props.multiple) {
      return undefined;
    }

    return (value: portModel[], getTagProps: AutocompleteRenderGetTagProps) =>
      value.map((option, index) => {
        const { key, ...tagProps } = getTagProps({ index });
        return <Chip variant="outlined" label={option.name} key={key} {...tagProps} />;
      });
  };

  useEffect(() => {
    refetch({
      variables: {
        where: filter,
        take: VISIBLE_OPTIONS_COUNT,
        skip: 0
      }
    });
  }, [filter, refetch]);

  useEffect(() => {
    setSelected(() => props.port || null);
  }, [props.port]);

  useEffect(() => {
    setSelectedPorts(() => (props.ports ? [...props.ports] : []));
  }, [props.ports]);

  useEffect(() => {
    apolloErrorHandler(error);
  }, [apolloErrorHandler, error]);

  useEffect(() => {
    let portOptions: portModel[] = data?.ports?.items || [];
    const totalCount = data?.ports?.totalCount || 0;
    if (totalCount > VISIBLE_OPTIONS_COUNT) {
      portOptions = [
        ...portOptions,
        {
          id: 'port-hidden-options-remaining',
          name: capitalizeEveryWord(
            t(TXT_AND_MORE, {
              count: totalCount - VISIBLE_OPTIONS_COUNT
            })
          )
        }
      ];
    }
    if (!_.isNil(selected)) {
      portOptions = [{ ...selected }, ...portOptions];
    }
    setOptions(() => [..._.uniqBy(portOptions, 'id')]);
    if (!!props.portIds) {
      setSelectedPorts(() => portOptions.filter((x) => props.portIds?.includes(x.id ?? '')));
    }
  }, [data, selected, t]);

  return (
    <>
      {openCreate ? (
        <PortEditor {...selected} name={selected?.name || text} onClose={openCloseEditor} afterSave={onCreated} />
      ) : null}
      <Autocomplete
        id={props.id || 'port-selector'}
        onChange={onSelectedChange}
        onInputChange={onInputChange}
        value={props.multiple ? selectedPorts : selected}
        getOptionDisabled={(option) => option.id === 'port-hidden-options-remaining'}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => `${option.name} (${option.country})`}
        options={options}
        loading={loading}
        multiple={props.multiple}
        filterOptions={(x) => x}
        renderInput={(params) => (
          <TextField
            {...params}
            label={capitalizeEveryWord(props.label ?? '')}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <Fragment>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                  {props.mustHaveCoordinates && selected && (!selected.latitude || !selected.longitude) ? (
                    <IconButton size="small" onClick={openCloseEditor} color="warning">
                      <EditIcon fontSize="inherit" />
                    </IconButton>
                  ) : null}
                </Fragment>
              )
            }}
            error={props.error}
            helperText={props.error ? props.errorMessage : ''}
          />
        )}
        renderOption={renderPortOption}
        disabled={props.disabled}
        noOptionsText={noOption()}
        renderTags={renderTags()}
      />
    </>
  );
};
